/* eslint-disable no-restricted-imports */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { default as lodashGet } from 'lodash.get';
import isEqual from 'lodash.isequal';

/**
 * Merge objects together, with the latter object taking precedence
 */
export const mergeObjects = <TObj extends {}>(
  initial: TObj,
  ...obs: TObj[]
): TObj =>
  obs.reduce<TObj>((acc, current) => ({ ...acc, ...current }), initial);

/**
 * Convert an array or object to NDJson
 *
 * @param records - Array or object to convert
 * @returns NDJson string
 */
export function toNDJson<T extends {}>(records: T[] | T): string {
  const data = Array.isArray(records) ? records : [records];
  return data.map((record) => JSON.stringify(record)).join('\n');
}

/**
 * Encodes an object to a base64 string
 *
 * @param obj - The object to encode
 */
export function encodeObject<T>(obj: T): string {
  const userContextString = JSON.stringify(obj).toString();
  const data = Buffer.from(userContextString).toString('base64');

  return data;
}
/**
 * Converts the object string to an object
 *
 * @param objString - A base64 string of the auth context
 */
export function decodeObject<T>(objString: string): T {
  const userContextString = Buffer.from(objString, 'base64').toString('utf-8');
  const context = JSON.parse(userContextString) as T;

  return context;
}

export const removeUndefinedValues = <T>(value: T) =>
  JSON.parse(JSON.stringify(value));

export const get = <T = any>(obj: T, path: any) => {
  return lodashGet(obj, path);
};

export const isPartialMatch = <T>(a: T, b: Partial<T>): boolean => {
  const bKeys = Object.keys(b) as (keyof T)[];

  return bKeys.every((key) => {
    const aValue = a[key];
    const bValue = b[key];

    return isEqual(aValue, bValue);
  });
};

export type StringKeyType<T> = {
  [K in keyof T]: T[K] extends string ? T[K] : never;
}[keyof T];

export function createObjectMap<T, K extends string | number>(
  items: T[],
  mapper: (item: T) => K,
) {
  return items.reduce(
    (memo, item) => {
      const key = mapper(item);
      return {
        ...memo,
        [key]: item,
      };
    },
    {} as Record<K, T>,
  );
}

export type GetObjectPath<T> = {
  [K in keyof T]: K extends string
    ? T[K] extends Record<string, unknown>
      ? `${K}.${GetObjectPath<T[K]>}` | K
      : K
    : never;
}[keyof T];

export type GetObjectPathValue<
  T,
  P extends GetObjectPath<T>,
> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? Rest extends GetObjectPath<T[K]>
      ? GetObjectPathValue<T[K], Rest>
      : never
    : never
  : P extends keyof T
    ? T[P]
    : never;
